import type { AxiosInstance } from 'axios';
import type { TranslateFn, U2000ApiErrorResult } from '../u2000Core/axios/U2000HandleApiError';
import type { U2000SearchPayload } from '../u2000Core/components/tables/basic/U2000BasicTable';
import { U2000GenerateUrlUUID } from '../u2000Core/tools/U2000UrlHelpers';

const LOGICALUNIT_URL = 'U3206';

type LogicalUnitEndpointsType = {
  INITLU: string;
  SEARCH: string;
  GET: (id: string | number) => string;
  CREATE: string;
  UPDATE: (id: string | number) => string;
  DELETE: (id: string | number) => string;
  AUDITS: (id: string | number) => string;
  TOKEN_USAGE: (id: string | number) => string;
  COPY_DATA_ACCESS: (id: string | number) => string;
};

const LogicalUnitEndpoints: LogicalUnitEndpointsType = {
  INITLU: `${LOGICALUNIT_URL}/initLU`,
  SEARCH: `${LOGICALUNIT_URL}/search`,
  GET: (id: string | number) => `${LOGICALUNIT_URL}/${id}`,
  CREATE: `${LOGICALUNIT_URL}/create`,
  UPDATE: (id: string | number) => `${LOGICALUNIT_URL}/update/${id}`,
  DELETE: (id: string | number) => `${LOGICALUNIT_URL}/delete/${id}`,
  AUDITS: (id: string | number) => `${LOGICALUNIT_URL}/${id}/audits`,
  TOKEN_USAGE: (id: string | number) => `${LOGICALUNIT_URL}/${id}/tokenUsage`,
  COPY_DATA_ACCESS: (id: string | number) => `${LOGICALUNIT_URL}/${id}/copyDataAccess`,
};

export type U3206DataAccessProfile = 'minimal' | 'project_manager' | 'full' | 'custom';

export interface U3206DataDomainRef {
  code: string;
  themeCode: string;
  labelFr: string;
  labelEn: string;
  descriptionFr?: string | null;
  descriptionEn?: string | null;
  exampleAllowedQuestions: string[];
  exampleBlockedQuestions: string[];
  defaultProfileCodes: string[];
  sortOrder: number;
}

export interface U3206DataAccessProfileRef {
  code: U3206DataAccessProfile;
  labelFr: string;
  labelEn: string;
}

export interface U3206DataAccessRow {
  id?: string;
  dataDomainCode: string;
  allowed: boolean;
  version?: number;
}

export interface U3206ProviderRef {
  id: string;
  code: string;
  name: string;
}

export interface U3206MissionRef {
  id: string;
  code: string;
  title: string;
  category: string;
  active: boolean;
}

export interface U3206UserRef {
  id: string;
  username: string;
  firstName: string;
  lastName: string;
  /** Groupe IA courant de l'usager (enrichissement initLU BL-0169), absent si non assigné. */
  assignedGroupId?: string | null;
  assignedGroupCode?: string | null;
  assignedGroupName?: string | null;
}

export interface U3206InitLU {
  launchSearch: boolean;
  hideSearch: boolean;
  nbRecordPerPage: number;
  logicalUnitCode: string;
  providers: U3206ProviderRef[];
  missions: U3206MissionRef[];
  users: U3206UserRef[];
  dataDomains: U3206DataDomainRef[];
  dataAccessProfiles: U3206DataAccessProfileRef[];
}

export interface U3206GroupProviderLink {
  id?: string;
  providerId: string;
  code?: string;
  name?: string;
  allowPersonalMissions: boolean;
  groupQuotaMonthlyTokens?: number | null;
  userQuotaMonthlyTokens?: number | null;
  version?: number;
}

export interface U3206TokenUsagePeriod {
  start: string;
  end: string;
  timezone: string;
}

export interface U3206TokenUsageMember {
  userId: string;
  username?: string;
  firstName?: string;
  lastName?: string;
  usageTokens: number;
}

export interface U3206TokenUsageProvider {
  providerId: string;
  code: string;
  name: string;
  groupQuotaMonthlyTokens: number | null;
  userQuotaMonthlyTokens: number | null;
  groupUsageTokens: number;
  members: U3206TokenUsageMember[];
}

export interface U3206TokenUsageReport {
  period: U3206TokenUsagePeriod;
  providers: U3206TokenUsageProvider[];
}

export interface U3206GroupMissionLink {
  id?: string;
  missionId: string;
  code?: string;
  title?: string;
  version?: number;
}

export interface U3206GroupMemberLink {
  id?: string;
  userId: string;
  username?: string;
  firstName?: string;
  lastName?: string;
  version?: number;
}

export interface U3206AiUserGroupSearchRow {
  id: string;
  code: string;
  name: string;
  description?: string | null;
  active: boolean;
  version: number;
}

export interface U3206AiUserGroupFormRow {
  id: string;
  code: string;
  name: string;
  description: string;
  active: boolean;
  version: number;
  dataAccessProfile: U3206DataAccessProfile;
  dataAccess: U3206DataAccessRow[];
  providers: U3206GroupProviderLink[];
  missions: U3206GroupMissionLink[];
  members: U3206GroupMemberLink[];
}

export interface U3206ProviderLinkPayload {
  id?: string;
  providerId: string;
  allowPersonalMissions: boolean;
  groupQuotaMonthlyTokens?: number | null;
  userQuotaMonthlyTokens?: number | null;
  version?: number;
}

export interface U3206AiUserGroupCreateBody {
  code: string;
  name: string;
  description?: string | null;
  active: boolean;
  providers?: U3206ProviderLinkPayload[];
  missions?: { missionId: string }[];
  members?: { userId: string }[];
}

export interface U3206DataAccessRowPayload {
  id?: string;
  dataDomainCode: string;
  allowed: boolean;
  version?: number;
}

export interface U3206AiUserGroupUpdateBody {
  id: string;
  code: string;
  name: string;
  description?: string | null;
  active: boolean;
  version: number;
  dataAccessProfile?: U3206DataAccessProfile;
  dataAccess?: U3206DataAccessRowPayload[];
  resetDataAccessToProfile?: boolean;
  providers?: U3206ProviderLinkPayload[];
  missions?: { id?: string; missionId: string; version?: number }[];
  members?: { id?: string; userId: string; version?: number }[];
}

export interface U3206CopyDataAccessBody {
  sourceGroupId: string;
}

export interface U3206AiUserGroupDeleteBody {
  id: string;
  version: number;
}

export const U3206_ERROR_CODE_MISSION_NOT_ASSISTANT = 'BU320601';
export const U3206_ERROR_CODE_PROVIDER_NOT_IN_ORG = 'BU320602';
export const U3206_ERROR_CODE_MISSION_NOT_ACCESSIBLE = 'BU320603';
export const U3206_ERROR_CODE_MEMBER_ALREADY_ASSIGNED = 'BU320604';
export const U3206_ERROR_CODE_DUPLICATE_CODE = 'BU320605';
export const U3206_ERROR_CODE_USER_NOT_IN_ORG = 'BU320606';
export const U3206_ERROR_CODE_UNKNOWN_DATA_DOMAIN = 'BU320607';
export const U3206_ERROR_CODE_SOURCE_GROUP_OUT_OF_ORG = 'BU320608';
export const U3206_ERROR_CODE_INVALID_DATA_ACCESS_PROFILE = 'BU320609';
export const U3206_ERROR_CODE_MEMBER_INACTIVE = 'BU320610';

export function u3206AssignedGroupLabel(
  user: Pick<U3206UserRef, 'assignedGroupCode' | 'assignedGroupName'>,
): string {
  const name = (user.assignedGroupName ?? '').trim();
  if (name) return name;
  return (user.assignedGroupCode ?? '').trim();
}

function u3206ErrorParameters(err: {
  parameters?: Array<{ name?: string; value?: unknown }>;
}): Record<string, string> {
  const out: Record<string, string> = {};
  for (const param of err.parameters ?? []) {
    if (!param?.name) continue;
    out[param.name] = String(param.value ?? '');
  }
  return out;
}

export function u3206FormatUserLabel(user: Pick<U3206UserRef, 'username' | 'firstName' | 'lastName'>): string {
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return name ? `${name} (${user.username})` : user.username;
}

function u3206NormalizeSearchQuery(query: string): string {
  return query.trim().toLowerCase();
}

/**
 * Filtre client pour l'autocomplete missions (initLU) : exclut les missions déjà liées au groupe.
 */
export function u3206FilterMissionsForAutocomplete(
  missions: U3206MissionRef[],
  linkedMissionIds: Iterable<string>,
  query: string,
): U3206MissionRef[] {
  const linked = new Set(linkedMissionIds);
  const q = u3206NormalizeSearchQuery(query);
  return missions.filter((mission) => {
    if (linked.has(mission.id)) return false;
    if (!q) return true;
    const haystack = `${mission.code} ${mission.title}`.toLowerCase();
    return haystack.includes(q);
  });
}

/**
 * Filtre client pour l'autocomplete usagers (initLU) :
 * - exclut les membres déjà liés au groupe courant ;
 * - exclut les usagers déjà assignés à un autre groupe (`assignedGroupId`).
 */
export function u3206FilterUsersForAutocomplete(
  users: U3206UserRef[],
  linkedUserIds: Iterable<string>,
  query: string,
  currentGroupId?: string | null,
): U3206UserRef[] {
  const linked = new Set(linkedUserIds);
  const q = u3206NormalizeSearchQuery(query);
  const groupId = currentGroupId?.trim() || '';
  return users.filter((user) => {
    if (linked.has(user.id)) return false;
    const assigned = user.assignedGroupId?.trim() || '';
    if (assigned && assigned !== groupId) return false;
    if (!q) return true;
    const haystack =
      `${user.username} ${user.firstName} ${user.lastName} ${user.assignedGroupCode ?? ''} ${user.assignedGroupName ?? ''}`.toLowerCase();
    return haystack.includes(q);
  });
}

export function createEmptyU3206FormRow(): U3206AiUserGroupFormRow {
  return {
    id: '',
    code: '',
    name: '',
    description: '',
    active: true,
    version: 0,
    dataAccessProfile: 'minimal',
    dataAccess: [],
    providers: [],
    missions: [],
    members: [],
  };
}

export function normalizeU3206AiUserGroupFromApi(row: U3206AiUserGroupFormRow): U3206AiUserGroupFormRow {
  return {
    id: row.id,
    code: row.code,
    name: row.name,
    description: row.description ?? '',
    active: row.active,
    version: row.version,
    dataAccessProfile: row.dataAccessProfile ?? 'minimal',
    dataAccess: (row.dataAccess ?? []).map((link) => ({
      id: link.id,
      dataDomainCode: link.dataDomainCode,
      allowed: link.allowed,
      version: link.version,
    })),
    providers: (row.providers ?? []).map((link) => ({
      id: link.id,
      providerId: link.providerId,
      code: link.code,
      name: link.name,
      allowPersonalMissions: link.allowPersonalMissions ?? false,
      groupQuotaMonthlyTokens: link.groupQuotaMonthlyTokens ?? null,
      userQuotaMonthlyTokens: link.userQuotaMonthlyTokens ?? null,
      version: link.version,
    })),
    missions: (row.missions ?? []).map((link) => ({
      id: link.id,
      missionId: link.missionId,
      code: link.code,
      title: link.title,
      version: link.version,
    })),
    members: (row.members ?? []).map((link) => ({
      id: link.id,
      userId: link.userId,
      username: link.username,
      firstName: link.firstName,
      lastName: link.lastName,
      version: link.version,
    })),
  };
}

function u3206BuildProviderPayload(links: U3206GroupProviderLink[]): U3206ProviderLinkPayload[] {
  return links.map((link) => {
    const item: U3206ProviderLinkPayload = {
      providerId: link.providerId,
      allowPersonalMissions: link.allowPersonalMissions,
      groupQuotaMonthlyTokens: link.groupQuotaMonthlyTokens ?? null,
      userQuotaMonthlyTokens: link.userQuotaMonthlyTokens ?? null,
    };
    if (link.id) item.id = link.id;
    if (link.version != null) item.version = link.version;
    return item;
  });
}

function u3206BuildMissionPayload(links: U3206GroupMissionLink[]) {
  return links.map((link) => {
    const item: { id?: string; missionId: string; version?: number } = { missionId: link.missionId };
    if (link.id) item.id = link.id;
    if (link.version != null) item.version = link.version;
    return item;
  });
}

function u3206BuildMemberPayload(links: U3206GroupMemberLink[]) {
  return links.map((link) => {
    const item: { id?: string; userId: string; version?: number } = { userId: link.userId };
    if (link.id) item.id = link.id;
    if (link.version != null) item.version = link.version;
    return item;
  });
}

function u3206BuildDataAccessPayload(links: U3206DataAccessRow[]): U3206DataAccessRowPayload[] {
  return links.map((link) => {
    const item: U3206DataAccessRowPayload = {
      dataDomainCode: link.dataDomainCode,
      allowed: link.allowed,
    };
    if (link.id) item.id = link.id;
    if (link.version != null) item.version = link.version;
    return item;
  });
}

export function buildCreateBody(data: U3206AiUserGroupFormRow): U3206AiUserGroupCreateBody {
  const desc = data.description.trim();
  return {
    code: data.code.trim().toUpperCase(),
    name: data.name.trim(),
    description: desc === '' ? null : desc,
    active: data.active,
    providers: u3206BuildProviderPayload(data.providers),
    missions: u3206BuildMissionPayload(data.missions),
    members: u3206BuildMemberPayload(data.members),
  };
}

export function buildUpdateBody(data: U3206AiUserGroupFormRow): U3206AiUserGroupUpdateBody {
  const desc = data.description.trim();
  return {
    id: data.id,
    code: data.code.trim().toUpperCase(),
    name: data.name.trim(),
    description: desc === '' ? null : desc,
    active: data.active,
    version: data.version,
    dataAccessProfile: data.dataAccessProfile,
    dataAccess: u3206BuildDataAccessPayload(data.dataAccess),
    providers: u3206BuildProviderPayload(data.providers),
    missions: u3206BuildMissionPayload(data.missions),
    members: u3206BuildMemberPayload(data.members),
  };
}

export function buildDeleteBody(data: U3206AiUserGroupFormRow): U3206AiUserGroupDeleteBody {
  return { id: data.id, version: data.version };
}

export const doInitLU = (axios: AxiosInstance) => {
  const params = { b: U2000GenerateUrlUUID() };
  return axios.get(LogicalUnitEndpoints.INITLU, { params });
};

export const doSearch = (axios: AxiosInstance, searchPayload: U2000SearchPayload) =>
  axios.post(LogicalUnitEndpoints.SEARCH, searchPayload);

export const doGetData = (axios: AxiosInstance, id: string | number, forceRefresh?: boolean) => {
  const params = { ...(forceRefresh && { b: U2000GenerateUrlUUID() }) };
  return axios.get(LogicalUnitEndpoints.GET(id), { params });
};

export const doCreateData = (axios: AxiosInstance, body: U3206AiUserGroupCreateBody) =>
  axios.post(LogicalUnitEndpoints.CREATE, body);

export const doUpdateData = (axios: AxiosInstance, id: string | number, body: U3206AiUserGroupUpdateBody) =>
  axios.post(LogicalUnitEndpoints.UPDATE(id), body);

export const doDeleteData = (axios: AxiosInstance, id: string | number, body: U3206AiUserGroupDeleteBody) =>
  axios.post(LogicalUnitEndpoints.DELETE(id), body);

export const doGetAuditsData = (axios: AxiosInstance, id: string | number) => {
  const params = { b: U2000GenerateUrlUUID() };
  return axios.get(LogicalUnitEndpoints.AUDITS(id), { params });
};

export const doGetTokenUsage = (axios: AxiosInstance, id: string | number) => {
  const params = { b: U2000GenerateUrlUUID() };
  return axios.get<{ result: U3206TokenUsageReport }>(LogicalUnitEndpoints.TOKEN_USAGE(id), { params });
};

export const doCopyDataAccess = (
  axios: AxiosInstance,
  id: string | number,
  body: U3206CopyDataAccessBody,
) => axios.post(LogicalUnitEndpoints.COPY_DATA_ACCESS(id), body);

export function luHandleAppiError(t: TranslateFn, nameSpace: string, err: unknown, errorResult: U2000ApiErrorResult) {
  const apiErr = err as {
    errorCode?: string;
    description?: string;
    parameters?: Array<{ name?: string; value?: unknown }>;
  };
  const code = apiErr?.errorCode ?? '';
  const title = t('errorTitle', { ns: nameSpace });

  if (code === U3206_ERROR_CODE_MEMBER_ALREADY_ASSIGNED) {
    const params = u3206ErrorParameters(apiErr);
    const description = (apiErr.description ?? '').trim();
    const userName = (params.userName ?? '').trim();
    const groupLabel = (params.groupName ?? '').trim() || (params.groupCode ?? '').trim();
    // Directives BL-0169 : message avec nom + groupe (pas toast générique B0003 / « déjà existant »).
    let text = '';
    if (userName && groupLabel) {
      text = t('errMemberAlreadyAssigned', { ns: nameSpace, userName, groupName: groupLabel });
    } else if (description) {
      text = description;
    } else {
      text = t('errMemberAlreadyAssignedFallback', { ns: nameSpace });
    }
    errorResult.genericErrors = [...(errorResult.genericErrors ?? []), { title, text }];
    return errorResult;
  }

  const keyByCode: Record<string, string> = {
    [U3206_ERROR_CODE_MISSION_NOT_ASSISTANT]: 'errMissionNotAssistant',
    [U3206_ERROR_CODE_PROVIDER_NOT_IN_ORG]: 'errProviderNotInOrg',
    [U3206_ERROR_CODE_MISSION_NOT_ACCESSIBLE]: 'errMissionNotAccessible',
    [U3206_ERROR_CODE_DUPLICATE_CODE]: 'errDuplicateCode',
    [U3206_ERROR_CODE_USER_NOT_IN_ORG]: 'errUserNotInOrg',
    [U3206_ERROR_CODE_UNKNOWN_DATA_DOMAIN]: 'errUnknownDataDomain',
    [U3206_ERROR_CODE_SOURCE_GROUP_OUT_OF_ORG]: 'errSourceGroupOutOfOrg',
    [U3206_ERROR_CODE_INVALID_DATA_ACCESS_PROFILE]: 'errInvalidDataAccessProfile',
    [U3206_ERROR_CODE_MEMBER_INACTIVE]: 'errMemberInactive',
  };
  const messageKey = keyByCode[code];
  if (messageKey) {
    errorResult.genericErrors = [
      ...(errorResult.genericErrors ?? []),
      { title, text: t(messageKey, { ns: nameSpace }) },
    ];
  }
  return errorResult;
}
